"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTrack = exports.useVirtualPageview = exports.trackingProvider = exports.virtualPageview = exports.withTrack = exports.track = exports.TrackingContext = exports.Track = exports.TrackingProvider = void 0;
var components_1 = require("./components");
Object.defineProperty(exports, "TrackingProvider", { enumerable: true, get: function () { return components_1.TrackingProvider; } });
Object.defineProperty(exports, "Track", { enumerable: true, get: function () { return components_1.Track; } });
Object.defineProperty(exports, "TrackingContext", { enumerable: true, get: function () { return components_1.TrackingContext; } });
var decorators_1 = require("./decorators");
Object.defineProperty(exports, "track", { enumerable: true, get: function () { return decorators_1.track; } });
Object.defineProperty(exports, "trackingProvider", { enumerable: true, get: function () { return decorators_1.trackingProvider; } });
Object.defineProperty(exports, "virtualPageview", { enumerable: true, get: function () { return decorators_1.virtualPageview; } });
Object.defineProperty(exports, "withTrack", { enumerable: true, get: function () { return decorators_1.withTrack; } });
var hooks_1 = require("./hooks");
Object.defineProperty(exports, "useTrack", { enumerable: true, get: function () { return hooks_1.useTrack; } });
Object.defineProperty(exports, "useVirtualPageview", { enumerable: true, get: function () { return hooks_1.useVirtualPageview; } });
/**
 * @description An instance of this class is the default export of
 * `@optimizely/segment-sdk-wrapper`.
 *
 * To use this Segment SDK wrapper, you'll need to initialize the Segment
 * SDK snippet in the entrypoint of your JS project using
 * {@linkcode SegmentSDKWrapper.init}. You'll want to execute this as early as
 * possible in your project's runtime lifecycle to ensure that all Segment
 * events are captured:
 *
 * ```javascript
 * import SegmentSDKWrapper, { Track, TrackingProvider } from '@optimizely/segment-sdk-wrapper';
 *
 * // Initialize the Segment SDK wrapper
 * SegmentSDKWrapper.init({
 *   // Set dynamic closure-scoped global properties that change during runtime
 *   getDynamicProperties: () => ({
 *     numberOfProjects: flux.evaluateToJS(ProjectGetters.entityCache).length,
 *   }),
 *
 *   // Set static closure-scoped global properties that are declared statically
 *   staticProperties: {
 *     environment: 'production',
 *   },
 *
 *   // Set closure-scoped global options
 *   getOptions: () => ({
 *     myOption: true,
 *   }),
 * });
 *
 * // Then, start tracking!
 * SegmentSDKWraper.track('created_rollout');
 *
 * const App = (
 *   <TrackingProvider>
 *     <Track eventName="saved_rollout">
 *       <button>Save</button>
 *     </Track>
 *   </TrackingProvider>
 * );
 * ```
 */
var SegmentSDKWrapper = /** @class */ (function () {
    function SegmentSDKWrapper() {
        /**
         * @description Internal singleton state that represents whether or not the
         * SDK wrapper instance has been intialized using the
         * {@linkcode SegmentSDKWrapper.init} method.
         */
        this.didInitializeWrapperInstance = false;
        /**
         * @description Getter function that is passed by the consumer upon invoking
         * {@linkcode SegmentSDKWrapper.init}. This function is used to determine the
         * global properties (scoped to this JavaScript closure) that should be
         * applied when an event is dispatched to Segment. This function is invoked
         * on each event dispatch, meaning that if a value returned by this method
         * changes at runtime, the updated value will be sent with all subsequent
         * events.
         *
         * Please note: these properties can be overwritten by properties in
         * {@link SegmentSDKWrapperInitializationOptions.staticProperties} or
         * passed directly to a Segment track method call. For a given event dispatch,
         * properties are applied in the following order:
         *
         * ```javascript
         * {
         *   ...dynamicProperties,  // Lowest precedence <-- from getDynamicProperties
         *   ...staticProperties,
         *   ...propertiesPassedDirectlyToTrackCall, // Highest precedence
         * }
         * ```
         */
        this.getDynamicProperties = function () { return ({}); };
        /**
         * @description Map of static properties that is passed by the consumer upon
         * invoking {@linkcode SegmentSDKWrapper.init} and can be updated at runtime
         * using {@linkcode SegmentSDKWrapper.setStaticProperty} and
         * {@linkcode SegmentSDKWrapper.unsetStaticProperty}.
         *
         * Please note: these properties can overwrite properties returned by
         * {@link SegmentSDKWrapperInitializationOptions.getDynamicProperties},
         * and can be overwritten by properties passed directly to a Segment track
         * method call. For a given event dispatch, properties are applied in the
         * following order:
         *
         * ```javascript
         * {
         *   ...dynamicProperties,  // Lowest precedence
         *   ...staticProperties, // <-- from staticProperties
         *   ...propertiesPassedDirectlyToTrackCall, // Highest precedence
         * }
         * ```
         */
        this.staticProperties = {};
        /**
         * @description Getter function that returns options to be applied to all
         * Segment events dispatched from this JavaScript closure.
         *
         * Please note: these properties can be overwritten by options passed directly
         * to a Segment SDK track method call.
         */
        this.getOptions = function () { return ({}); };
    }
    /**
     * @description Returns either a direct reference to the Segment SDK snippet
     * API on the page (if available and the wrapper is initialized) or the
     * stubbed version of that API. If the real Segment SDK snippet API is not
     * returned, a warning will be logged to the console.
     */
    SegmentSDKWrapper.prototype.getSegmentInstance = function () {
        if (this.didInitializeWrapperInstance &&
            window &&
            window.analytics &&
            typeof window.analytics === "object") {
            return window.analytics;
        }
        if (console && console.warn && !window.__TEST__) {
            if (!this.didInitializeWrapperInstance) {
                console.warn("Segment SDK wrapper is not initialized.");
            }
            else {
                console.warn("Segment snippet is not available on this page.");
            }
        }
        return SegmentSDKWrapper.segmentInstanceStub;
    };
    /**
     * @description Initialization method for the Segment SDK wrapper singleton.
     * Call this method as early as possible in your frontend lifecycle in order
     * to capture all Segment events that are dispatched from this JavaScript
     * closure:
     *
     * ```javascript
     * SegmentSDKWrapper.init({
     *   // Set dynamic closure-scoped global properties that change during runtime
     *   getDynamicProperties: () => ({
     *     numberOfProjects: flux.evaluateToJS(ProjectGetters.entityCache).length,
     *   }),
     *
     *   // Set static closure-scoped global properties that are declared statically
     *   staticProperties: {
     *     environment: 'production',
     *   },
     *
     *   // Set closure-scoped global options
     *   getOptions: () => ({
     *     myOption: true,
     *   }),
     * });
     * ```
     *
     * For more details on how the available initialization options accepted by
     * this method, please see {@link SegmentSDKWrapperInitializationOptions}.
     *
     * @category Initialization
     */
    SegmentSDKWrapper.prototype.init = function (options) {
        if (options === void 0) { options = {}; }
        if (this.didInitializeWrapperInstance) {
            if (console && console.warn && !window.__TEST__) {
                console.warn("Segment SDK wrapper was already initialized in this closure!");
            }
            return;
        }
        this.didInitializeWrapperInstance = true;
        var _a = options.getDynamicProperties, getDynamicProperties = _a === void 0 ? (function () { return ({}); }) : _a, _b = options.staticProperties, staticProperties = _b === void 0 ? {} : _b, _c = options.getOptions, getOptions = _c === void 0 ? (function () { return ({}); }) : _c;
        this.getDynamicProperties = getDynamicProperties;
        this.staticProperties = staticProperties;
        this.getOptions = getOptions;
    };
    /**
     * @description Getter method for retrieving the global properties, both
     * static and dynamic, for this JavaScript closure. These global properties
     * are applied to all events tracked by Segment in this JavaScript closure.
     *
     * Properties are merged using the following order of precedence:
     *
     * ```javascript
     * {
     *   ...dynamicProperties, // Lower precedence
     *   ...staticProperties, // Higher precedence
     * }
     * ```
     *
     * To see how dynamic properties are set, see
     * {@linkcode SegmentSDKWrapper.init}.
     *
     * To see how static properties are set, see
     * {@linkcode SegmentSDKWrapper.init},
     * {@linkcode SegmentSDKWrapper.setStaticProperty}, and
     * {@linkcode SegmentSDKWrapper.unsetStaticProperty}.
     *
     * @category Context Management
     */
    SegmentSDKWrapper.prototype.getProperties = function () {
        return __assign(__assign({}, this.getDynamicProperties()), this.staticProperties);
    };
    /**
     * @description Setter method for defining a static property to be applied to
     * all Segment events dispatched by this JavaScript closure.
     *
     * Static properties can also be set in {@linkcode SegmentSDKWrapper.init},
     * and can be unset by {@linkcode SegmentSDKWrapper.unsetStaticProperty}.
     *
     * @category Context Management
     */
    SegmentSDKWrapper.prototype.setStaticProperty = function (propertyKey, propertyValue) {
        this.staticProperties[propertyKey] = propertyValue;
    };
    /**
     * @description Setter method for deleting a static property being applied to
     * Segment events dispatched by this JavaScript closure.
     *
     * Static properties are set in {@linkcode SegmentSDKWrapper.init}
     * and {@linkcode SegmentSDKWrapper.setStaticProperty}.
     *
     * @category Context Management
     */
    SegmentSDKWrapper.prototype.unsetStaticProperty = function (propertyKey) {
        delete this.staticProperties[propertyKey];
    };
    /**
     * @description The `identify` method is how you link your users, and their
     * actions, to a recognizable userId and traits. Segment recommends against
     * using identify for anonymous visitors to your site.
     *
     * Options passed to this method will be merged with this closure's global
     * options returned by
     * {@link SegmentSDKWrapperInitializationOptions.getOptions}, and take
     * precedence over global options. See {@linkcode SegmentSDKWrapper.init} for
     * more details.
     *
     * Example:
     *
     * ```javascript
     * SegmentSDKWrapper.identify('12091906-01011992', {
     *   name: 'Grace Hopper',
     *   email: 'grace@usnavy.gov'
     * });
     * ```
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#identify
     *
     * @category Tracking
     */
    SegmentSDKWrapper.prototype.identify = function (userId, traits, options, callback) {
        this.getSegmentInstance().identify(userId, traits, __assign(__assign({}, this.getOptions()), options), callback);
    };
    /**
     * @description The `page` method lets you record page views on your website,
     * along with optional extra information about the page being viewed.
     *
     * Options passed to this method will be merged with this closure's global
     * options returned by
     * {@link SegmentSDKWrapperInitializationOptions.getOptions}, and take
     * precedence over global options. See {@linkcode SegmentSDKWrapper.init} for
     * more details.
     *
     * Properties passed to this method will be merged with this closure's global
     * properties, both static and dynamic, and take precedence over global
     * properties. See {@linkcode SegmentSDKWrapper.getProperties} for more details.
     *
     * Example:
     *
     * ```javascript
     * SegmentSDKWrapper.page('Product Details', 'Pricing', {
     *   title: 'Segment Pricing',
     *   url: 'https://segment.com/pricing',
     *   path: '/pricing',
     *   referrer: 'https://segment.com/warehouses'
     * });
     * ```
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
     *
     * @category Tracking
     */
    SegmentSDKWrapper.prototype.page = function (category, name, properties, options, callback) {
        this.getSegmentInstance().page(category, name, __assign(__assign({}, this.getProperties()), properties), __assign(__assign({}, this.getOptions()), options), callback);
    };
    /**
     * @description The `track` method lets you record actions your users perform.
     *
     * Options passed to this method will be merged with this closure's global
     * options returned by
     * {@link SegmentSDKWrapperInitializationOptions.getOptions}, and take
     * precedence over global options. See {@linkcode SegmentSDKWrapper.init} for
     * more details.
     *
     * Properties passed to this method will be merged with this closure's global
     * properties, both static and dynamic, and take precedence over global
     * properties. See {@linkcode SegmentSDKWrapper.getProperties} for more details.
     *
     * Example:
     *
     * ```javascript
     * SegmentSDKWrapper.track('Article Completed', {
     *   title: 'How to Create a Tracking Plan',
     *   course: 'Intro to Analytics',
     * });
     * ```
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track
     *
     * @category Tracking
     */
    SegmentSDKWrapper.prototype.track = function (event, properties, options, callback) {
        this.getSegmentInstance().track(event, __assign(__assign({}, this.getProperties()), properties), __assign(__assign({}, this.getOptions()), options), callback);
    };
    /**
     * @description `trackLink` is a helper method that attaches the track call as
     * a handler to a `link`. With `trackLink` a short timeout (300 ms) is
     * inserted to give the `track` call more time. This is useful when a page
     * would redirect before the `track` method could complete all requests.
     *
     * Properties passed to this method will be merged with this closure's global
     * properties, both static and dynamic, and take precedence over global
     * properties. See {@linkcode SegmentSDKWrapper.getProperties} for more details.
     *
     * Example:
     *
     * ```javascript
     * const link = document.getElementById('free-trial-link');
     *
     * SegmentSDKWrapper.trackLink(link, 'Clicked Free-Trial Link', {
     *   plan: 'Enterprise',
     * });
     * ```
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track-link
     *
     * @category Tracking
     */
    SegmentSDKWrapper.prototype.trackLink = function (element, event, properties) {
        var normalizedProperties = typeof properties === "function" ? properties() : properties;
        this.getSegmentInstance().trackLink(element, event, __assign(__assign({}, this.getProperties()), normalizedProperties));
    };
    /**
     * @description `trackForm` is a helper method that binds a track call to a
     * form submission. The `trackForm` method inserts a short timeout (300 ms)
     * to give the `track` call more time to complete. This is useful to prevent
     * a page from redirecting before the `track` method could complete all
     * requests.
     *
     * Properties passed to this method will be merged with this closure's global
     * properties, both static and dynamic, and take precedence over global
     * properties. See {@linkcode SegmentSDKWrapper.getProperties} for more details.
     *
     * Example:
     *
     * ```javascript
     * const form = document.getElementById('signup-form');
     *
     * SegmentSDKWrapper.trackForm(form, 'Signed Up', {
     *   plan: 'Premium',
     *   revenue: 99.00
     * });
     * ```
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track-form
     *
     * @category Tracking
     */
    SegmentSDKWrapper.prototype.trackForm = function (form, event, properties) {
        var normalizedProperties = typeof properties === "function" ? properties() : properties;
        this.getSegmentInstance().trackForm(form, event, __assign(__assign({}, this.getProperties()), normalizedProperties));
    };
    /**
     * @description The `group` method associates an identified user with a
     * company, organization, project, workspace, team, tribe, platoon,
     * assemblage, cluster, troop, gang, party, society or any other collective
     * noun you come up with for the same concept.
     *
     * Options passed to this method will be merged with this closure's global
     * options returned by
     * {@link SegmentSDKWrapperInitializationOptions.getOptions}, and take
     * precedence over global options. See {@linkcode SegmentSDKWrapper.init} for
     * more details.
     *
     * Example:
     *
     * ```javascript
     * SegmentSDKWrapper.group('UNIVAC Working Group', {
     *   principles: ['Eckert', 'Mauchly'],
     *   site: 'Eckert–Mauchly Computer Corporation',
     *   statedGoals: 'Develop the first commercial computer',
     *   industry: 'Technology'
     * });
     * ```
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#group
     *
     * @category Tracking
     */
    SegmentSDKWrapper.prototype.group = function (groupId, traits, options, callback) {
        this.getSegmentInstance().group(groupId, traits, __assign(__assign({}, this.getOptions()), options), callback);
    };
    /**
     * @description The `alias` method combines two previously unassociated user
     * identities. Segment usually handles aliasing automatically when you call
     * `identify` on a user, however some tools require an explicit `alias` call.
     *
     * Options passed to this method will be merged with this closure's global
     * options returned by
     * {@link SegmentSDKWrapperInitializationOptions.getOptions}, and take
     * precedence over global options. See {@linkcode SegmentSDKWrapper.init} for
     * more details.
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#alias
     *
     * @category Tracking
     */
    SegmentSDKWrapper.prototype.alias = function (userId, previousId, options, callback) {
        this.getSegmentInstance().alias(userId, previousId, __assign(__assign({}, this.getOptions()), options), callback);
    };
    /**
     * @description The `ready` method allows you to pass in a method that is
     * called once Segment finishes initializing, and once all enabled device-mode
     * destinations load. It’s like jQuery’s `ready` method, except for
     * destinations.
     *
     * Example:
     *
     * ```javascript
     * SegmentSDKWrapper.ready(() => {
     *   window.mixpanel.set_config({ verbose: true });
     * });
     * ```
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#ready
     *
     * @category Advanced
     */
    SegmentSDKWrapper.prototype.ready = function (callback) {
        this.getSegmentInstance().ready(callback);
    };
    /**
     * @description Calling the `debug` method turns on debug mode, which logs
     * helpful messages to the console. Refresh the page after you invoke `debug`
     * to see the messages.
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#debug
     *
     * @category Advanced
     */
    SegmentSDKWrapper.prototype.debug = function (enabled) {
        if (enabled === void 0) { enabled = true; }
        this.getSegmentInstance().debug(enabled);
    };
    /**
     * @description The Segment snippet emits events whenever you call `alias`,
     * `group`, `identify`, `track` or `page`. Use the `on` method to set
     * listeners for these events and run your own custom code. This can be useful
     * if you want to send data to a service for which Segment does not have a
     * destination.
     *
     * Example:
     *
     * ```javascript
     * SegmentSDKWrapper.on('track', function(event, properties, options) {
     *   bigdataTool.push(['recordEvent', event]);
     * });
     * ```
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#emitter
     *
     * @category Advanced
     */
    SegmentSDKWrapper.prototype.on = function (method, callback) {
        this.getSegmentInstance().on(method, callback);
    };
    /**
     * @description The `timeout` method sets the length (in milliseconds) of the
     * callbacks and helper functions. This is helpful if you have multiple
     * scripts that need to fire in your callback or `trackLink`, `trackForm`
     * helper function.
     *
     * Example:
     *
     * ```javascript
     * SegmetnSDKWrapper.timeout(500);
     * ```
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#extending-timeout
     *
     * @category Advanced
     */
    SegmentSDKWrapper.prototype.timeout = function (milliseconds) {
        this.getSegmentInstance().timeout(milliseconds);
    };
    /**
     * @description Calling `reset` resets the `id`, including `anonymousId`, and
     * clears `traits` for the currently identified user and group.
     *
     * This method also clears the initialization state of this Segment SDK
     * Wrapper singleton instance, including dynamic/static properties, global
     * options, and the internal initialization flag.
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#reset-or-logout
     *
     * @category Advanced
     */
    SegmentSDKWrapper.prototype.reset = function () {
        this.getDynamicProperties = (function () { return ({}); });
        this.staticProperties = {};
        this.getOptions = (function () { return ({}); });
        this.getSegmentInstance().reset();
        this.didInitializeWrapperInstance = false;
    };
    /**
     * @description You can pass an `integrations` object in the `options` of
     * {@linkcode SegmentSDKWrapper.alias}, {@linkcode SegmentSDKWrapper.group},
     * {@linkcode SegmentSDKWrapper.identify}, {@linkcode SegmentSDKWrapper.page}
     * and {@linkcode SegmentSDKWrapper.track} methods to send data to only the
     * selected destinations. By default all destinations are enabled.
     *
     * Options passed to this method will be merged with this closure's global
     * options returned by
     * {@link SegmentSDKWrapperInitializationOptions.getOptions}, and take
     * precedence over global options. See {@linkcode SegmentSDKWrapper.init} for
     * more details.
     *
     * Example:
     *
     * ```javascript
     * analytics.load(
     *   'writekey',
     *   {
     *     integrations: {
     *       All: false,
     *       'Google Analytics': true,
     *       'Segment.io': true
     *     },
     *   },
     * );
     * ```
     *
     * For more information, see Segment's docs on this method:
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#managing-data-flow-with-the-integrations-object
     *
     * @category Advanced
     */
    SegmentSDKWrapper.prototype.load = function (writeKey, options) {
        this.getSegmentInstance().load(writeKey, __assign(__assign({}, this.getOptions()), options));
    };
    /**
     * @description Dispatches a {@linkcode SegmentSDKWrapper.page} call for
     * virtual router page views.
     *
     * Options passed to this method will be merged with this closure's global
     * options returned by
     * {@link SegmentSDKWrapperInitializationOptions.getOptions}, and take
     * precedence over global options. See {@linkcode SegmentSDKWrapper.init} for
     * more details.
     *
     * Properties passed to this method will be merged with this closure's global
     * properties, both static and dynamic, and take precedence over global
     * properties. See {@linkcode SegmentSDKWrapper.getProperties} for more details.
     *
     * @category Tracking
     */
    SegmentSDKWrapper.prototype.virtualPageview = function (category, name, urlSuffix, properties, options, callback) {
        this.page(category, name, __assign(__assign({ url: "" + window.location.href + urlSuffix, isVirtual: true }, this.getProperties()), properties), __assign(__assign({}, this.getOptions()), options), callback);
    };
    /**
     * @description Stub version of the Segment SDK core API. If the Segment SDK
     * wrapper isn't invoked or the Segment SDK snippet is not available on the
     * page, this stub is used to handle method invocations instead.
     */
    SegmentSDKWrapper.segmentInstanceStub = {
        identify: function () { return undefined; },
        page: function () { return undefined; },
        track: function () { return undefined; },
        trackLink: function () { return undefined; },
        trackForm: function () { return undefined; },
        group: function () { return undefined; },
        alias: function () { return undefined; },
        ready: function () { return undefined; },
        debug: function () { return undefined; },
        on: function () { return undefined; },
        timeout: function () { return undefined; },
        reset: function () { return undefined; },
        load: function () { return undefined; },
    };
    return SegmentSDKWrapper;
}());
exports.default = new SegmentSDKWrapper();
